import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { MdSearch } from "react-icons/md";
import { getCategoryIdByName } from "../../../utils/categoryResolver";

function BeritaPopuler({ categoryName = "Berita" }) {
	const navigate = useNavigate();
	const [cards, setCards] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [categories, setCategories] = useState([]);

	const fetchPostsData = useCallback(async () => {
		try {
			const catId = (await getCategoryIdByName(categoryName)) || null;
			const params = catId ? { category_id: catId } : {};
			const response = await axios.get(`/api_w/posts`, {
				params,
				headers: { db_name: process.env.REACT_APP_DB_NAME },
			});
			const filteredPosts = response.data.data.filter(
				(post) => post.status === 1
			);
			const sortedCards = [...filteredPosts].sort(
				(a, b) => parseInt(b.views || 0) - parseInt(a.views || 0)
			);
			setCards(sortedCards.slice(0, 4));

			const uniqueCats = [
				...new Set(
					filteredPosts.map((p) => p.PostCategory?.name).filter(Boolean)
				),
			];
			setCategories(uniqueCats);
		} catch (error) {
			toast.error(error.response?.data?.message || error.message);
		}
	}, [categoryName]);

	useEffect(() => {
		fetchPostsData();
	}, [fetchPostsData]);

	const handleSearch = () => {
		if (searchQuery.trim()) {
			navigate(`/berita?search=${encodeURIComponent(searchQuery.trim())}`);
		}
	};

	const formatDate = (dateString) => {
		const options = { year: "numeric", month: "short", day: "numeric" };
		return new Date(dateString).toLocaleDateString("id-ID", options);
	};

	const truncateTitle = (title, limit) =>
		title.length > limit ? title.substring(0, limit) + "..." : title;

	const defaultTags = [
		"Madrasah Digital",
		"Cyber Madrasah",
		"Pendidikan",
		"JMD",
		"Kemenag DIY",
		"Teknologi",
	];
	const tags = categories.length > 0 ? categories : defaultTags;

	return (
		<section aria-label="Sidebar Berita" className="space-y-6 lg:sticky lg:top-24">
			{/* Search Box */}
			<div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
				<h2 className="font-bold text-gray-800 mb-4 text-sm uppercase tracking-wide">
					Cari Berita
				</h2>
				<div className="relative">
					<input
						type="text"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleSearch()}
						placeholder="Cari berita..."
						className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 pr-10 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-400 transition-all duration-200"
					/>
					<button
						onClick={handleSearch}
						onKeyDown={(e) => e.key === "Enter" && handleSearch()}
						aria-label="Cari"
						className="absolute right-1.5 top-1.5 bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg transition-colors duration-200"
					>
						<MdSearch className="w-4 h-4" />
					</button>
				</div>
			</div>

			{/* Berita Terbaru */}
			<div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
				<h3 className="font-bold text-gray-800 mb-5 text-sm uppercase tracking-wide flex items-center gap-2">
					<div className="w-1 h-5 bg-green-500 rounded-full" />
					Berita Terbaru
				</h3>
				<div className="space-y-4">
					{cards.slice(0, 4).map((news, idx) => (
						<Link
							key={news.id}
							to={`/berita/${news.slug}`}
							className="group flex gap-3 p-2 -mx-2 rounded-xl hover:bg-green-50/50 transition-colors duration-200"
						>
							<div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
								<img
									src={news.image}
									alt={news.title}
									className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
								/>
							</div>
							<div className="flex-1 min-w-0">
								<h4 className="text-sm font-semibold text-gray-800 group-hover:text-green-700 transition-colors duration-200 line-clamp-2 leading-snug">
									{truncateTitle(news.title, 55)}
								</h4>
								<p className="text-xs text-gray-400 mt-1.5 flex items-center gap-1">
									<svg
										className="w-3 h-3"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
										/>
									</svg>
									{formatDate(news.date)}
								</p>
							</div>
						</Link>
					))}
				</div>
			</div>

			{/* Tag Berita */}
			<div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
				<h3 className="font-bold text-gray-800 mb-4 text-sm uppercase tracking-wide flex items-center gap-2">
					<div className="w-1 h-5 bg-green-500 rounded-full" />
					Tag Berita
				</h3>
				<div className="flex flex-wrap gap-2">
					{tags.map((tag, idx) => (
						<span
							key={idx}
							className="inline-block bg-gray-50 hover:bg-green-50 text-gray-600 hover:text-green-700 border border-gray-200 hover:border-green-300 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-all duration-200"
						>
							{tag}
						</span>
					))}
				</div>
			</div>
		</section>
	);
}

export default BeritaPopuler;
